import type { PageContent } from "../types";

export const p_july_2026_ai_search_what_small_businesses_should_do_now: PageContent = {
  slug: "july-2026-ai-search-what-small-businesses-should-do-now",
  title: "July 2026: AI Search — What Small Businesses Should Do Now | Phil Carr SEO Consultant",
  description:
    "AI Overviews, ChatGPT Search and Perplexity are now part of how customers find local businesses. Here's a practical July 2026 checklist to stay visible and keep enquiries flowing.",
  date: "2026-07-08",
  body: `# July 2026: AI Search — What Small Businesses Should Do Now

**Published:** July 8, 2026

## TL;DR

AI-powered answers from Google's AI Overviews, ChatGPT Search and Perplexity are now a normal part of the customer journey. Smaller businesses that publish clear, well-structured content — with strong local signals — are being cited far more often than those still chasing keyword density.

## What changed in the first half of 2026

Google rolled AI Overviews out to more commercial queries through the spring, and ChatGPT Search continued to take measurable share of research-style queries. Click-through rates on classic blue links softened for informational content, but converting queries (e.g. "emergency electrician Barnsley") still rely heavily on the local pack and organic results.

The practical takeaway: you need to be visible in *both* the AI answer and the traditional results underneath it.

## Where citations are actually happening

Not every AI surface plays by the same rules. As of July 2026, here's the shape of what small UK businesses can realistically target:

- **Google AI Overviews** — biggest reach by far. Cites 3–8 sources per answer, favours pages with clear question-answer structure and strong entity signals (Organization + LocalBusiness schema, consistent NAP data, recognised author bylines).
- **ChatGPT Search** — heavier weighting on brand mentions and reputable third-party sources (industry press, Reddit, trade directories) than on your own site copy. Being talked about elsewhere matters as much as what you publish.
- **Perplexity** — the most citation-friendly. Explicit source links next to every claim. Rewards recent, well-structured, dated content.
- **Google Gemini** in the app — increasingly used for local recommendations. Google Business Profile data, review quality and review recency dominate.

You don't need to optimise for all four separately — the underlying signals overlap. But knowing which one your customers actually use should shape where you spend the effort.

## A simple July checklist

1. **Rewrite your top five pages with a clear answer in the first paragraph.** AI systems lift short, declarative sentences. Lead with the answer, then explain.
2. **Add or refresh Organization, LocalBusiness and FAQPage schema.** Structured data is now the dominant way LLMs disambiguate your brand.
3. **Audit your Google Business Profile.** New photos, an updated services list, and at least two fresh reviews go a long way.
4. **Publish one genuinely useful guide per month.** First-hand experience, a named author and real numbers — that's what gets cited.
5. **Check brand mentions across Reddit, YouTube and trade directories.** LLMs lean on these sources heavily when summarising "best in [area]" queries.

## Structured data patterns worth adding this month

If you only touch schema once this quarter, prioritise these four:

- **Organization + LocalBusiness** on every page (in the site-wide layout), with an \`@id\` anchor so LLMs can link entities back to a single node.
- **FAQPage** on service pages where you genuinely answer common questions — but only where the questions and answers are visible on the page. Hidden-only FAQ schema is now actively suppressed.
- **Article** with author, \`datePublished\` and \`dateModified\` on every blog post. LLMs use \`dateModified\` to decide whether a source is fresh enough to cite.
- **BreadcrumbList** on nested routes. It's a small win, but it noticeably improves how AI systems describe your site structure.

Skip the exotic types (HowTo, Course, JobPosting) unless they actually describe the page. Overstated schema now gets ignored — sometimes penalised.

## What to publish this quarter to earn AI citations

The content most likely to be cited in AI answers over the next 90 days shares three traits: it's specific to a place or a role, it contains a defensible opinion, and it shows first-hand experience. In practical terms, that's:

- **One "how much does X cost in [town]" guide** with real figures, updated for the current quarter.
- **One "how long does X take" piece** grounded in actual client timelines, not industry averages.
- **One "how to choose between X and Y" comparison** written from lived experience.

Three genuinely useful pieces will out-earn thirty templated blog posts. AI systems reward substance because their users punish thin answers.

## What to stop doing

Thin AI-spun blog posts, keyword-stuffed location pages and generic "10 best…" listicles aimed at no one in particular have become a liability. They dilute topical authority and rarely earn citations.

## Need a hand?

If you'd like a second opinion on where your site sits in the new AI search landscape, get in touch at info@phil-carr.co.uk or call 01226 697 325.

## Key Takeaways

- AI Overviews and ChatGPT Search are now mainstream — plan for both.
- Lead pages with a one-paragraph answer so LLMs can quote you.
- Structured data and a maintained Google Business Profile do the heavy lifting locally.
- Quality, first-hand content beats volume every time.
`,
};
